import { useMemo } from "react";
import {
  buildLegacyExeBottomNav,
  buildLegacyFieldBottomNav,
} from "@/app/nav/buildLegacyBottomNav";
import { getNavFlags, recordNavFallback } from "@/app/nav/navFlags";
import {
  resolveApiAccountHub,
  resolveApiBottomNav,
  type ResolvedAccountHubGroup,
} from "@/app/nav/resolveBottomNav";
import type { NavSource } from "@/app/nav/types";
import {
  accountHubGroups,
  type FieldCapability,
} from "@/app/field/fieldCapabilityRegistry";
import {
  exeAccountHubGroups,
  type ExeCapability,
} from "@/app/exe/exeCapabilityRegistry";
import { useNavQuery } from "@/modules/sys/queries/useNavQuery";
import { useAuth } from "@/shared/auth/AuthContext";
import { authRoleCodes } from "@/shared/auth/types";
import { hasAnyPermission, PermissionCodes } from "@/shared/auth/permissions";
import type { BottomNavItem } from "@/shared/ui/layout/BottomNavigation";
import type { ApiNavShell } from "@tm-food/api-contract";

export type ShellBottomNavState = {
  items: BottomNavItem[];
  sourceUsed: NavSource;
  isLoading: boolean;
  errorBanner: string | null;
};

function useShellBottomNav(
  shell: Extract<ApiNavShell, "field" | "exe">,
): ShellBottomNavState {
  const { user } = useAuth();
  const flags = useMemo(() => getNavFlags(), []);
  const permissions = useMemo(
    () => user?.permissions ?? [],
    [user?.permissions],
  );
  const roles = useMemo(() => authRoleCodes(user?.roles ?? []), [user?.roles]);
  const legacy = useMemo(
    () =>
      shell === "field"
        ? buildLegacyFieldBottomNav(permissions, roles)
        : buildLegacyExeBottomNav(permissions),
    [shell, permissions, roles],
  );

  const apiEnabled = flags.source === "api";
  const apiQuery = useNavQuery(shell, apiEnabled);
  const apiItems = useMemo(
    () => (apiQuery.data ? resolveApiBottomNav(apiQuery.data, shell) : null),
    [apiQuery.data, shell],
  );

  return useMemo((): ShellBottomNavState => {
    if (flags.source === "legacy") {
      recordNavFallback(`${shell}_source_legacy`);
      return {
        items: legacy,
        sourceUsed: "legacy",
        isLoading: false,
        errorBanner: null,
      };
    }
    if (apiItems) {
      let finalItems = apiItems;
      if (shell === "field") {
        // Filter out planned/obsolete cip tab from bottom nav
        finalItems = finalItems.filter((i) => i.id !== "cip");

        const canAccessBatches =
          roles.includes("ROLE_NV_SX") ||
          user?.username === "nv.sx" ||
          hasAnyPermission(permissions, [
            PermissionCodes.MesBatchList,
            PermissionCodes.MesBatchRead,
            PermissionCodes.MesOperationExecute,
          ]);

        if (canAccessBatches) {
          if (
            !finalItems.some(
              (i) => i.id === "batches" || i.route === "/field/batches",
            )
          ) {
            const homeIdx = finalItems.findIndex((i) => i.id === "home");
            const batchItem: BottomNavItem = {
              id: "batches",
              label: "Mẻ SX",
              route: "/field/batches",
              icon: "layers",
            };
            if (homeIdx >= 0) {
              const next = [...finalItems];
              next.splice(homeIdx + 1, 0, batchItem);
              finalItems = next;
            } else {
              finalItems = [...finalItems, batchItem];
            }
          }
        } else {
          finalItems = finalItems.filter(
            (i) => i.id !== "batches" && i.route !== "/field/batches",
          );
        }

        const canAccessPr =
          roles.includes("ROLE_NV_SX") ||
          user?.username === "nv.sx" ||
          hasAnyPermission(permissions, [
            PermissionCodes.PurchaseRequisitionList,
            PermissionCodes.PurchaseRequest,
            PermissionCodes.PurchaseManage,
          ]);

        if (canAccessPr) {
          const prItem: BottomNavItem = {
            id: "requisitions",
            label: "Đề nghị mua",
            route: "/field/pur/requisitions",
            icon: "clipboard-list",
          };

          // Ensure PR item is present exactly once for authorized users
          if (
            !finalItems.some(
              (i) =>
                i.id === "requisitions" ||
                i.route === "/field/pur/requisitions",
            )
          ) {
            const accIdx = finalItems.findIndex((i) => i.id === "account");
            if (accIdx >= 0) {
              const copy = [...finalItems];
              copy.splice(accIdx, 0, prItem);
              finalItems = copy;
            } else {
              finalItems = [...finalItems, prItem];
            }
          }
        } else {
          // Hide PR menu item completely if user lacks permissions
          finalItems = finalItems.filter(
            (i) =>
              i.id !== "requisitions" &&
              i.route !== "/field/pur/requisitions",
          );
        }

        // Deduplicate finalItems by id and route
        const seenIds = new Set<string>();
        const seenRoutes = new Set<string>();
        finalItems = finalItems.filter((i) => {
          if (seenIds.has(i.id) || seenRoutes.has(i.route)) return false;
          seenIds.add(i.id);
          seenRoutes.add(i.route);
          return true;
        });
      }
      return {
        items: finalItems,
        sourceUsed: "api",
        isLoading: false,
        errorBanner: null,
      };
    }
    if (apiQuery.isLoading || apiQuery.isFetching) {
      return {
        items: [],
        sourceUsed: "api",
        isLoading: true,
        errorBanner: null,
      };
    }
    if (apiQuery.isError) {
      if (flags.fallback) {
        recordNavFallback(`${shell}_api_error`);
        return {
          items: legacy,
          sourceUsed: "legacy",
          isLoading: false,
          errorBanner: null,
        };
      }
      return {
        items: [],
        sourceUsed: "api",
        isLoading: false,
        errorBanner: "Không tải được menu. Thử lại.",
      };
    }
    return {
      items: [],
      sourceUsed: "api",
      isLoading: false,
      errorBanner: null,
    };
  }, [
    flags.source,
    flags.fallback,
    apiItems,
    apiQuery.isLoading,
    apiQuery.isFetching,
    apiQuery.isError,
    legacy,
    shell,
    user,
    permissions,
    roles,
  ]);
}

export function useFieldBottomNav(): ShellBottomNavState {
  return useShellBottomNav("field");
}

export function useExeBottomNav(): ShellBottomNavState {
  return useShellBottomNav("exe");
}

export type FieldAccountHubState = {
  groups: {
    id: string;
    label: string;
    items: {
      screenId: string;
      path: string;
      label: string;
      icon: string;
      capabilityId?: string;
    }[];
  }[];
  sourceUsed: NavSource;
  errorBanner: string | null;
};

/** Field `/me` hub — API account_hub; legacy only via VITE_NAV_SOURCE=legacy. */
export function useFieldAccountHub(): FieldAccountHubState {
  const { user } = useAuth();
  const flags = useMemo(() => getNavFlags(), []);
  const permissions = useMemo(
    () => user?.permissions ?? [],
    [user?.permissions],
  );

  const legacyGroups = useMemo(() => {
    const raw = accountHubGroups(permissions, (granted, required) =>
      required.length === 0 ? true : hasAnyPermission(granted, required),
    );
    return raw.map((g) => ({
      id: g.group,
      label: g.label,
      items: g.items.map((c: FieldCapability) => ({
        screenId: c.screenId ?? c.id,
        path: c.path,
        label: c.label,
        icon: c.icon,
        capabilityId: c.id,
      })),
    }));
  }, [permissions]);

  const apiEnabled = flags.source === "api";
  const apiQuery = useNavQuery("field", apiEnabled);
  const apiGroups = useMemo(
    () => (apiQuery.data ? resolveApiAccountHub(apiQuery.data, "field") : null),
    [apiQuery.data],
  );

  return useMemo((): FieldAccountHubState => {
    if (flags.source === "legacy") {
      recordNavFallback("field_account_source_legacy");
      return { groups: legacyGroups, sourceUsed: "legacy", errorBanner: null };
    }
    if (apiGroups) {
      const isNvSx =
        authRoleCodes(user?.roles ?? []).includes("ROLE_NV_SX") ||
        user?.username === "nv.sx" ||
        hasAnyPermission(permissions, [
          PermissionCodes.PurchaseRequisitionList,
          PermissionCodes.PurchaseRequest,
        ]);

      const mappedGroups = apiGroups.map((g: ResolvedAccountHubGroup) => ({
        id: g.id,
        label: g.label,
        items: g.items.map((it) => ({
          ...it,
          capabilityId: capabilityIdForScreen(it.screenId),
        })),
      }));

      if (isNvSx) {
        const hasPr = mappedGroups.some((g) =>
          g.items.some(
            (it) =>
              it.screenId === "SCR-FIELD-PUR-PR" ||
              it.screenId === "SCR-PUR-PR" ||
              it.path === "/field/pur/requisitions",
          ),
        );
        if (!hasPr) {
          const prItem = {
            screenId: "SCR-FIELD-PUR-PR",
            path: "/field/pur/requisitions",
            label: "Đề nghị mua hàng",
            icon: "clipboard-list",
            detail: "Tạo & theo dõi đề nghị mua vật tư, NVL",
            capabilityId: "field.pur.requisitions",
          };
          const workGroup = mappedGroups.find(
            (g) => g.id === "field.work" || g.id === "work",
          );
          if (workGroup) {
            workGroup.items.push(prItem);
          } else {
            mappedGroups.unshift({
              id: "field.work",
              label: "Công việc",
              items: [prItem],
            });
          }
        }
      }

      const hasQms = mappedGroups.some((g) =>
        g.items.some(
          (it) =>
            it.screenId === "SCR-FIELD-QMS" ||
            it.path === "/field/qms",
        ),
      );
      if (!hasQms) {
        const qmsItem = {
          screenId: "SCR-FIELD-QMS",
          path: "/field/qms",
          label: "Chất lượng",
          icon: "clipboard-list",
          detail: "Kiểm tra chất lượng đầu vào (IQC) & đầu ra (FQC)",
          capabilityId: "field.qms.inbox",
        };
        const workGroup = mappedGroups.find(
          (g) => g.id === "field.work" || g.id === "work",
        );
        if (workGroup) {
          workGroup.items.push(qmsItem);
        } else {
          mappedGroups.unshift({
            id: "field.work",
            label: "Công việc",
            items: [qmsItem],
          });
        }
      }

      return {
        groups: mappedGroups,
        sourceUsed: "api",
        errorBanner: null,
      };
    }
    if (apiQuery.isError && flags.fallback) {
      recordNavFallback("field_account_api_error");
      return { groups: legacyGroups, sourceUsed: "legacy", errorBanner: null };
    }
    if (apiQuery.isError) {
      return {
        groups: [],
        sourceUsed: "api",
        errorBanner: "Không tải được menu. Thử lại.",
      };
    }
    return { groups: [], sourceUsed: "api", errorBanner: null };
  }, [
    flags.source,
    flags.fallback,
    apiGroups,
    apiQuery.isError,
    legacyGroups,
    permissions,
    user?.roles,
    user?.username,
  ]);
}

function capabilityIdForScreen(screenId: string): string | undefined {
  switch (screenId) {
    case "SCR-FIELD-PUR-PR":
    case "SCR-PUR-PR":
      return "field.pur.requisitions";
    case "SCR-MES-MY-ACTIVITY":
      return "field.mes.activity";
    case "SCR-SYS-NOTIFY":
      return "field.sys.notify";
    case "SCR-MES-HANDOVER":
      return "field.mes.handover";
    case "SCR-MES-REWORK":
      return "field.mes.rework";
    case "SCR-WMS-FIELD":
      return "field.wms.home";
    case "SCR-SYS-PROFILE":
      return "field.sys.profile";
    case "SCR-SYS-SETTINGS":
      return "field.sys.settings";
    case "SCR-SYS-SYNC":
      return "field.sys.sync";
    case "SCR-EXE-NOTIFY":
      return "exe.sys.notify";
    case "SCR-EXE-PROFILE":
      return "exe.sys.profile";
    case "SCR-EXE-SETTINGS":
      return "exe.sys.settings";
    case "SCR-EXE-APPROVALS":
      return "exe.approvals.hub";
    case "SCR-FIELD-QMS":
      return "field.qms.inbox";
    case "SCR-EXE-QMS":
      return "exe.qms.inbox";
    default:
      return undefined;
  }
}

export type ExeAccountHubState = FieldAccountHubState;

/** EXE `/me` hub — API account_hub (shell=exe). */
export function useExeAccountHub(): ExeAccountHubState {
  const { user } = useAuth();
  const flags = useMemo(() => getNavFlags(), []);
  const permissions = useMemo(
    () => user?.permissions ?? [],
    [user?.permissions],
  );

  const legacyGroups = useMemo(() => {
    const raw = exeAccountHubGroups(permissions, (granted, required) =>
      required.length === 0 ? true : hasAnyPermission(granted, required),
    );
    return raw.map((g) => ({
      id: g.group,
      label: g.label,
      items: g.items.map((c: ExeCapability) => ({
        screenId: c.screenId ?? c.id,
        path: c.path,
        label: c.label,
        icon: c.icon,
        capabilityId: c.id,
      })),
    }));
  }, [permissions]);

  const apiEnabled = flags.source === "api";
  const apiQuery = useNavQuery("exe", apiEnabled);
  const apiGroups = useMemo(
    () => (apiQuery.data ? resolveApiAccountHub(apiQuery.data, "exe") : null),
    [apiQuery.data],
  );

  return useMemo((): ExeAccountHubState => {
    if (flags.source === "legacy") {
      recordNavFallback("exe_account_source_legacy");
      return { groups: legacyGroups, sourceUsed: "legacy", errorBanner: null };
    }
    if (apiGroups) {
      const mapped = apiGroups.map((g: ResolvedAccountHubGroup) => ({
        id: g.id,
        label: g.label,
        items: g.items.map((it) => ({
          ...it,
          capabilityId: capabilityIdForScreen(it.screenId),
        })),
      }));
      const itemCount = mapped.reduce((n, g) => n + g.items.length, 0);
      // Published snapshot may lag seed (exe.account) — prefer live hub over empty API.
      if (itemCount === 0 && legacyGroups.length > 0) {
        recordNavFallback("exe_account_api_empty");
        return { groups: legacyGroups, sourceUsed: "legacy", errorBanner: null };
      }

      const hasQms = mapped.some((g) =>
        g.items.some(
          (it) =>
            it.screenId === "SCR-EXE-QMS" ||
            it.path === "/exe/qms",
        ),
      );
      if (!hasQms) {
        const qmsItem = {
          screenId: "SCR-EXE-QMS",
          path: "/exe/qms",
          label: "Chất lượng",
          icon: "check-square",
          detail: "Kiểm tra chất lượng đầu vào (IQC) & đầu ra (FQC)",
          capabilityId: "exe.qms.inbox",
        };
        const workGroup = mapped.find(
          (g) => g.id === "exe.work" || g.id === "work",
        );
        if (workGroup) {
          workGroup.items.push(qmsItem);
        } else {
          mapped.push({
            id: "exe.work",
            label: "Công việc",
            items: [qmsItem],
          });
        }
      }

      return { groups: mapped, sourceUsed: "api", errorBanner: null };
    }
    if (apiQuery.isError && flags.fallback) {
      recordNavFallback("exe_account_api_error");
      return { groups: legacyGroups, sourceUsed: "legacy", errorBanner: null };
    }
    if (apiQuery.isError) {
      return {
        groups: [],
        sourceUsed: "api",
        errorBanner: "Không tải được menu. Thử lại.",
      };
    }
    // Still loading API — show legacy so Me is never blank.
    if (legacyGroups.length > 0) {
      return { groups: legacyGroups, sourceUsed: "legacy", errorBanner: null };
    }
    return { groups: [], sourceUsed: "api", errorBanner: null };
  }, [
    flags.source,
    flags.fallback,
    apiGroups,
    apiQuery.isError,
    legacyGroups,
  ]);
}
